import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useGetQuestionBankSubjectDetailsQuery,
  useUpdateQuestionBankSubjectMutation,
} from "../../slices/questionBankSubjectApiSlice";

const QuestionBankSubjectEditScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Quantitative Aptitude");
  const [isActive, setIsActive] = useState(true);

  const {
    data: subject,
    isLoading,
    error,
  } = useGetQuestionBankSubjectDetailsQuery(id);

  const [updateQuestionBankSubject, { isLoading: loadingUpdate }] =
    useUpdateQuestionBankSubjectMutation();

  useEffect(() => {
    if (subject) {
      setName(subject.name || "");
      setDescription(subject.description || "");
      setCategory(subject.category || "Quantitative Aptitude");
      setIsActive(subject.isActive);
    }
  }, [subject]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Subject name is required");
      return;
    }

    try {
      await updateQuestionBankSubject({
        subjectId: id,
        name: name.trim(),
        description: description.trim(),
        category,
        isActive,
      }).unwrap();

      toast.success("Question bank subject updated successfully");

      navigate("/question-bank-subjects");
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.error ||
          "Failed to update question bank subject",
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/question-bank-subjects"
          className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-xl bg-white shadow hover:bg-blue-50 text-blue-600 transition"
        >
          ← Back to Question Bank Subjects
        </Link>

        <div className="bg-white/80 backdrop-blur-lg shadow-2xl rounded-3xl border border-white/30 p-6 md:p-10">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
              Edit Question Bank Subject
            </h1>

            <p className="mt-2 text-gray-500">
              Update the details of the question bank subject
            </p>
          </div>

          {(isLoading || loadingUpdate) && (
            <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
              {isLoading
                ? "Loading subject..."
                : "Updating question bank subject..."}
            </div>
          )}

          {error && (
            <div className="mb-6 rounded-xl bg-red-100 p-4 text-center text-red-700">
              {error?.data?.message || error?.error || "Failed to load subject"}
            </div>
          )}

          {!isLoading && subject && (
            <form onSubmit={submitHandler} className="space-y-6">
              {/* Subject Name */}
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Subject Name <span className="text-red-500">*</span>
                </label>

                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter subject name"
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Category
                </label>

                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Quantitative Aptitude">
                    Quantitative Aptitude
                  </option>
                  <option value="Reasoning">Reasoning</option>
                  <option value="General Awareness">General Awareness</option>
                  <option value="English">English</option>
                  <option value="Computer Knowledge">Computer Knowledge</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Description
                </label>

                <textarea
                  rows={5}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description..."
                  className="w-full resize-none rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-5 w-5 rounded text-blue-600 focus:ring-blue-500"
                />

                <label htmlFor="isActive" className="font-medium text-gray-700">
                  Active Subject
                </label>
              </div>

              {/* Buttons */}
              <div className="flex flex-col gap-4 sm:flex-row">
                <button
                  type="submit"
                  disabled={loadingUpdate}
                  className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3.5 text-lg font-semibold text-white shadow-lg transition hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60"
                >
                  {loadingUpdate ? "Updating..." : "Update Subject"}
                </button>

                <Link
                  to="/question-bank-subjects"
                  className="flex-1 rounded-xl border border-gray-300 bg-white py-3.5 text-center text-lg font-semibold text-gray-700 transition hover:bg-gray-100"
                >
                  Cancel
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionBankSubjectEditScreen;
